import { httpsCallable } from 'firebase/functions';
import { normalizeOrderAmounts } from '../utils/orderAmounts';

const CHECKOUT_ATTEMPT_STORAGE_KEY = 'koralKrush.paymentV2CheckoutAttempt';
const CHECKOUT_ATTEMPT_SCHEMA_VERSION = 2;
const CHECKOUT_ATTEMPT_STATES = new Set(['preparing', 'ready', 'submitted']);
const MAX_STRIPE_AMOUNT = 99_999_999;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export class PaymentContractError extends Error {
  constructor(message) {
    super(message);
    this.name = 'PaymentContractError';
  }
}

function requireNonEmptyString(value, fieldName) {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new PaymentContractError(`${fieldName} is required.`);
  }
  return value.trim();
}

function requireUUID(value, fieldName) {
  const normalized = requireNonEmptyString(value, fieldName).toLowerCase();
  if (!UUID_PATTERN.test(normalized)) {
    throw new PaymentContractError(`${fieldName} must be a canonical UUID.`);
  }
  return normalized;
}

function requireMinorAmount(
  value,
  fieldName,
  { allowZero = true, maximum = MAX_STRIPE_AMOUNT } = {},
) {
  if (
    !Number.isSafeInteger(value) ||
    value < 0 ||
    value > maximum ||
    (!allowZero && value === 0)
  ) {
    throw new PaymentContractError(
      `${fieldName} must be a ${allowZero ? 'non-negative' : 'positive'} integer in the currency's minor unit.`,
    );
  }
  return value;
}

function requireBoundedString(value, fieldName, maximum) {
  if (typeof value !== 'string' || value.length > maximum) {
    throw new PaymentContractError(`${fieldName} is invalid.`);
  }
  return value;
}

function normalizeCurrency(value) {
  const currency = requireNonEmptyString(value, 'currency').toUpperCase();
  if (!/^[A-Z]{3}$/.test(currency)) {
    throw new PaymentContractError('currency must be a three-letter ISO code.');
  }
  return currency;
}

function normalizeCartItem(item, index) {
  const productId = requireNonEmptyString(item?.productID, `items[${index}].productId`);
  const quantity = item?.quantity;
  if (!Number.isSafeInteger(quantity) || quantity < 1) {
    throw new PaymentContractError(`items[${index}].quantity must be a positive integer.`);
  }

  const selectedOptionIds = (item?.selectedModifiers || []).map((modifier, modifierIndex) =>
    requireNonEmptyString(
      modifier?.optionID,
      `items[${index}].selectedOptionIds[${modifierIndex}]`,
    ),
  );
  if (new Set(selectedOptionIds).size !== selectedOptionIds.length) {
    throw new PaymentContractError(`items[${index}] contains a duplicate selected option.`);
  }

  return {
    productId,
    quantity,
    ...(selectedOptionIds.length > 0 ? { selectedOptionIds } : {}),
  };
}

/**
 * Build the only client-authored portion of a v2 quote. Prices, tax, currency,
 * ownership, availability, and totals are deliberately absent and must be
 * supplied or verified by the server.
 */
export function buildCheckoutQuoteV2Request({
  checkoutAttemptId,
  storeId,
  items,
  customerContact,
  customInstructions,
  someoneElsePickingUp,
  pickupName,
  pickupPhone,
}) {
  const normalizedAttemptId = requireUUID(checkoutAttemptId, 'checkoutAttemptId');
  const normalizedStoreId = requireNonEmptyString(storeId, 'storeId');
  if (!Array.isArray(items) || items.length === 0) {
    throw new PaymentContractError('Your cart is empty.');
  }

  const name = requireNonEmptyString(customerContact?.name, 'customerContact.name');
  const email = requireNonEmptyString(customerContact?.email, 'customerContact.email');
  if (!email.includes('@')) {
    throw new PaymentContractError('customerContact.email is invalid.');
  }
  const phone = typeof customerContact?.phone === 'string'
    ? customerContact.phone.trim()
    : '';
  const instructions = typeof customInstructions === 'string'
    ? customInstructions.trim()
    : '';

  const fulfillment = {
    method: 'pickup',
    ...(instructions ? { instructions } : {}),
  };
  if (someoneElsePickingUp) {
    const pickupContactName = requireNonEmptyString(pickupName, 'fulfillment.pickupContact.name');
    const normalizedPickupPhone = typeof pickupPhone === 'string' ? pickupPhone.trim() : '';
    fulfillment.pickupContact = {
      name: pickupContactName,
      ...(normalizedPickupPhone ? { phone: normalizedPickupPhone } : {}),
    };
  }

  return {
    checkoutAttemptId: normalizedAttemptId,
    storeId: normalizedStoreId,
    items: items.map(normalizeCartItem),
    customerContact: {
      name,
      email: email.toLowerCase(),
      ...(phone ? { phone } : {}),
    },
    fulfillment,
  };
}

export function parseCheckoutQuoteV2Response(payload, expectedCheckoutAttemptId = null) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new PaymentContractError('The quote service returned an invalid response.');
  }

  const quoteId = requireNonEmptyString(payload.quoteId, 'quoteId');
  const checkoutAttemptId = requireUUID(payload.checkoutAttemptId, 'checkoutAttemptId');
  if (
    expectedCheckoutAttemptId != null &&
    checkoutAttemptId !== requireUUID(expectedCheckoutAttemptId, 'expectedCheckoutAttemptId')
  ) {
    throw new PaymentContractError('The quote is bound to a different checkout attempt.');
  }
  const subtotalMinor = requireMinorAmount(payload.subtotalMinor, 'subtotalMinor');
  const taxMinor = requireMinorAmount(payload.taxMinor, 'taxMinor');
  const totalValue = payload.totalMinor ?? payload.amountMinor;
  const totalMinor = requireMinorAmount(totalValue, 'totalMinor');
  if (subtotalMinor + taxMinor !== totalMinor) {
    throw new PaymentContractError('The quote totals are internally inconsistent.');
  }
  if (payload.amountMinor != null && payload.amountMinor !== totalMinor) {
    throw new PaymentContractError('The quote amount does not match its total.');
  }
  if (!Array.isArray(payload.items) || payload.items.length === 0 || payload.items.length > 100) {
    throw new PaymentContractError('The quote does not contain any verified items.');
  }

  const quoteHash = requireNonEmptyString(payload.quoteHash, 'quoteHash');
  if (!/^[0-9a-f]{64}$/.test(quoteHash)) {
    throw new PaymentContractError('The quote hash is invalid.');
  }
  const paymentCollection = requireNonEmptyString(
    payload.paymentCollection,
    'paymentCollection',
  );
  const captureMethod = requireNonEmptyString(payload.captureMethod, 'captureMethod');
  if (paymentCollection === 'stripe') {
    if (captureMethod !== 'automatic_async' || totalMinor < 50) {
      throw new PaymentContractError(
        'The quote did not use a valid Stripe collection policy.',
      );
    }
  } else if (paymentCollection === 'not_required') {
    if (
      captureMethod !== 'none' ||
      subtotalMinor !== 0 ||
      taxMinor !== 0 ||
      totalMinor !== 0
    ) {
      throw new PaymentContractError(
        'A payment-free quote must contain exact zero amounts and no Stripe capture policy.',
      );
    }
  } else {
    throw new PaymentContractError('The quote payment collection policy is invalid.');
  }

  const currency = normalizeCurrency(payload.currency);
  if (
    ['ISK', 'UGX'].includes(currency) &&
    [subtotalMinor, taxMinor, totalMinor].some(amount => amount % 100 !== 0)
  ) {
    throw new PaymentContractError(`${currency} quote amounts must end in 00.`);
  }

  return {
    quoteId,
    checkoutAttemptId,
    subtotalMinor,
    taxMinor,
    totalMinor,
    amountMinor: totalMinor,
    currency,
    quoteHash,
    paymentCollection,
    captureMethod,
    expiresAt: payload.expiresAt ?? null,
    items: payload.items,
  };
}

export function parsePaymentIntentV2Response(payload, quote, expectedStripeMode) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new PaymentContractError('The payment service returned an invalid response.');
  }
  if (!quote?.quoteId) {
    throw new PaymentContractError('A verified quote is required before creating payment.');
  }
  if (
    quote.paymentCollection !== 'stripe' ||
    quote.captureMethod !== 'automatic_async' ||
    quote.totalMinor < 50
  ) {
    throw new PaymentContractError('This quote is not eligible for Stripe payment collection.');
  }

  const quoteId = requireNonEmptyString(payload.quoteId, 'quoteId');
  if (quoteId !== quote.quoteId) {
    throw new PaymentContractError('The payment response is bound to a different quote.');
  }

  const amountMinor = requireMinorAmount(payload.amountMinor, 'amountMinor', { allowZero: false });
  if (amountMinor !== quote.totalMinor) {
    throw new PaymentContractError('The payment amount changed after the quote was accepted.');
  }

  const currency = normalizeCurrency(payload.currency);
  if (currency !== quote.currency) {
    throw new PaymentContractError('The payment currency changed after the quote was accepted.');
  }

  const stripeMode = requireNonEmptyString(payload.stripeMode, 'stripeMode').toLowerCase();
  const configuredMode = requireNonEmptyString(expectedStripeMode, 'expectedStripeMode').toLowerCase();
  if (!['test', 'live'].includes(stripeMode) || !['test', 'live'].includes(configuredMode)) {
    throw new PaymentContractError('Stripe mode must be either test or live.');
  }
  if (stripeMode !== configuredMode) {
    throw new PaymentContractError('The server Stripe mode does not match this client build.');
  }

  const captureMethod = requireNonEmptyString(payload.captureMethod, 'captureMethod');
  if (captureMethod !== quote.captureMethod) {
    throw new PaymentContractError('The payment capture method changed after quoting.');
  }

  const paymentIntentId = requireNonEmptyString(payload.paymentIntentId, 'paymentIntentId');
  const clientSecret = requireNonEmptyString(payload.clientSecret, 'clientSecret');
  const customerId = requireNonEmptyString(payload.customerId, 'customerId');
  const customerSessionClientSecret = requireNonEmptyString(
    payload.customerSessionClientSecret,
    'customerSessionClientSecret',
  );
  const status = requireNonEmptyString(payload.status, 'status');
  if (!paymentIntentId.startsWith('pi_') || !clientSecret.startsWith(`${paymentIntentId}_secret_`)) {
    throw new PaymentContractError('The payment identifiers are inconsistent.');
  }
  if (!customerId.startsWith('cus_') || !customerSessionClientSecret.startsWith('cuss_')) {
    throw new PaymentContractError('The customer session identifiers are invalid.');
  }
  if (![
    'requires_payment_method',
    'requires_confirmation',
    'requires_action',
    'processing',
    'requires_capture',
    'canceled',
    'succeeded',
  ].includes(status)) {
    throw new PaymentContractError('The PaymentIntent returned an unsupported status.');
  }
  if (['ISK', 'UGX'].includes(currency) && amountMinor % 100 !== 0) {
    throw new PaymentContractError(`${currency} payment amounts must end in 00.`);
  }

  return {
    quoteId,
    paymentIntentId,
    clientSecret,
    customerId,
    customerSessionClientSecret,
    amountMinor,
    currency,
    stripeMode,
    captureMethod,
    status,
  };
}

export function parseStoredCheckoutQuoteV2Document(
  payload,
  expectedQuoteId,
  expectedUserId,
) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new PaymentContractError('The stored payment quote is invalid.');
  }
  if (payload.schemaVersion !== 2) {
    throw new PaymentContractError('The stored payment quote has an unsupported schema.');
  }
  const userId = requireNonEmptyString(payload.userId, 'quote.userId');
  if (userId !== requireNonEmptyString(expectedUserId, 'expectedUserId')) {
    throw new PaymentContractError('The stored payment quote belongs to another account.');
  }
  const quoteHash = requireNonEmptyString(payload.quoteHash, 'quote.quoteHash');
  if (!/^[0-9a-f]{64}$/.test(quoteHash)) {
    throw new PaymentContractError('The stored payment quote hash is invalid.');
  }
  const quote = parseCheckoutQuoteV2Response({
    ...payload.immutable,
    quoteHash,
  });
  if (quote.quoteId !== requireNonEmptyString(expectedQuoteId, 'expectedQuoteId')) {
    throw new PaymentContractError('The stored payment quote identifier changed.');
  }
  const paymentIntentId = payload.paymentIntentId == null
    ? null
    : requireNonEmptyString(payload.paymentIntentId, 'quote.paymentIntentId');
  if (
    (quote.paymentCollection === 'stripe' &&
      (!paymentIntentId || !paymentIntentId.startsWith('pi_'))) ||
    (quote.paymentCollection === 'not_required' && paymentIntentId != null)
  ) {
    throw new PaymentContractError('The stored payment binding is invalid.');
  }
  return { ...quote, quoteHash, paymentIntentId, userId };
}

function exactQuoteLineBinding(line, currency, index) {
  const selectedOptions = Array.isArray(line?.selectedOptions)
    ? line.selectedOptions
    : null;
  if (!selectedOptions) {
    throw new PaymentContractError(`quote.items[${index}].selectedOptions is invalid.`);
  }
  if (selectedOptions.length > 50) {
    throw new PaymentContractError(`quote.items[${index}].selectedOptions is too large.`);
  }
  const lineId = requireNonEmptyString(line?.lineId, `quote.items[${index}].lineId`);
  if (!/^line_[0-9a-f]{20}$/.test(lineId)) {
    throw new PaymentContractError(`quote.items[${index}].lineId is invalid.`);
  }
  const quantity = requireMinorAmount(
    line?.quantity,
    `quote.items[${index}].quantity`,
    { allowZero: false, maximum: 1_000 },
  );
  const unitAmountMinor = requireMinorAmount(
    line?.unitAmountMinor,
    `quote.items[${index}].unitAmountMinor`,
  );
  const lineTotalMinor = requireMinorAmount(
    line?.lineTotalMinor,
    `quote.items[${index}].lineTotalMinor`,
  );
  const lineCurrency = normalizeCurrency(line?.currency);
  if (lineCurrency !== currency) {
    throw new PaymentContractError(`quote.items[${index}].currency is invalid.`);
  }
  const options = selectedOptions.map((option, optionIndex) => ({
    id: requireNonEmptyString(
      option?.optionId,
      `quote.items[${index}].selectedOptions[${optionIndex}].optionId`,
    ),
    groupId: requireNonEmptyString(
      option?.groupId,
      `quote.items[${index}].selectedOptions[${optionIndex}].groupId`,
    ),
    title: requireBoundedString(
      option?.title,
      `quote.items[${index}].selectedOptions[${optionIndex}].title`,
      200,
    ),
    unitAmountMinor: requireMinorAmount(
      option?.unitAmountMinor,
      `quote.items[${index}].selectedOptions[${optionIndex}].unitAmountMinor`,
    ),
    currency,
  }));
  const identities = options.map(option => `${option.groupId}\u0000${option.id}`);
  if (new Set(identities).size !== identities.length) {
    throw new PaymentContractError(`quote.items[${index}] contains duplicate modifiers.`);
  }
  const modifiersMinor = options.reduce((total, option) => total + option.unitAmountMinor, 0);
  const calculatedTotal = (unitAmountMinor + modifiersMinor) * quantity;
  if (!Number.isSafeInteger(calculatedTotal) || calculatedTotal !== lineTotalMinor) {
    throw new PaymentContractError(`quote.items[${index}] total is invalid.`);
  }
  return {
    lineId,
    productId: requireNonEmptyString(line?.productId, `quote.items[${index}].productId`),
    productName: requireBoundedString(
      line?.productName,
      `quote.items[${index}].productName`,
      200,
    ),
    description: requireBoundedString(
      line?.description,
      `quote.items[${index}].description`,
      1_000,
    ),
    unitAmountMinor,
    currency: lineCurrency,
    quantity,
    lineTotalMinor,
    selectedOptions: options,
  };
}

function exactOrderLineBinding(line, currency, index) {
  const selectedOptions = Array.isArray(line?.selectedOptions)
    ? line.selectedOptions
    : null;
  if (!selectedOptions) {
    throw new PaymentContractError(`order.items[${index}].selectedOptions is invalid.`);
  }
  if (selectedOptions.length > 50) {
    throw new PaymentContractError(`order.items[${index}].selectedOptions is too large.`);
  }
  const quantity = requireMinorAmount(
    line?.quantity,
    `order.items[${index}].quantity`,
    { allowZero: false, maximum: 1_000 },
  );
  const unitAmountMinor = requireMinorAmount(
    line?.unitAmount,
    `order.items[${index}].unitAmount`,
  );
  const lineTotalMinor = requireMinorAmount(
    line?.totalAmount,
    `order.items[${index}].totalAmount`,
  );
  const lineCurrency = normalizeCurrency(line?.currency);
  if (lineCurrency !== currency) {
    throw new PaymentContractError(`order.items[${index}].currency is invalid.`);
  }
  const options = selectedOptions.map((option, optionIndex) => ({
    id: requireNonEmptyString(
      option?.id,
      `order.items[${index}].selectedOptions[${optionIndex}].id`,
    ),
    groupId: requireNonEmptyString(
      option?.groupID,
      `order.items[${index}].selectedOptions[${optionIndex}].groupID`,
    ),
    title: requireBoundedString(
      option?.title,
      `order.items[${index}].selectedOptions[${optionIndex}].title`,
      200,
    ),
    unitAmountMinor: requireMinorAmount(
      option?.unitAmount,
      `order.items[${index}].selectedOptions[${optionIndex}].unitAmount`,
    ),
    currency: normalizeCurrency(option?.currency),
  }));
  const modifiersMinor = options.reduce((total, option) => total + option.unitAmountMinor, 0);
  const calculatedTotal = (unitAmountMinor + modifiersMinor) * quantity;
  if (!Number.isSafeInteger(calculatedTotal) || calculatedTotal !== lineTotalMinor) {
    throw new PaymentContractError(`order.items[${index}] total is invalid.`);
  }
  return {
    lineId: requireNonEmptyString(line?.id, `order.items[${index}].id`),
    productId: requireNonEmptyString(line?.productID, `order.items[${index}].productID`),
    productName: requireBoundedString(
      line?.productName,
      `order.items[${index}].productName`,
      200,
    ),
    description: requireBoundedString(
      line?.description,
      `order.items[${index}].description`,
      1_000,
    ),
    unitAmountMinor,
    currency: lineCurrency,
    quantity,
    lineTotalMinor,
    selectedOptions: options,
  };
}

/** Bind a materialized server order to the exact immutable quote before cart mutation. */
export function validateMaterializedOrderV2(order, documentId, quote) {
  if (!order || typeof order !== 'object' || Array.isArray(order) || !quote?.quoteId) {
    throw new PaymentContractError('The confirmed order is invalid.');
  }
  const orderId = requireNonEmptyString(documentId, 'order.documentId');
  const aliases = [order.id, order.orderID, order.checkoutQuoteId];
  const orderPaymentIntentId = order.paymentIntentId ?? null;
  const quotePaymentIntentId = quote.paymentIntentId ?? null;
  if (
    order.schemaVersion !== 2 ||
    orderId !== quote.quoteId ||
    aliases.some(value => value !== quote.quoteId) ||
    order.quoteHash !== quote.quoteHash ||
    order.paymentCollection !== quote.paymentCollection ||
    orderPaymentIntentId !== quotePaymentIntentId ||
    order.userId !== quote.userId ||
    order.userID !== quote.userId
  ) {
    throw new PaymentContractError('The confirmed order binding does not match its quote.');
  }
  const currency = normalizeCurrency(order.currency);
  const amounts = normalizeOrderAmounts(order);
  if (
    currency !== quote.currency ||
    amounts.subtotalAmount !== quote.subtotalMinor ||
    amounts.taxAmount !== quote.taxMinor ||
    amounts.totalAmount !== quote.totalMinor
  ) {
    throw new PaymentContractError('The confirmed order amounts do not match its quote.');
  }
  if (quote.paymentCollection === 'stripe') {
    if (
      quote.captureMethod !== 'automatic_async' ||
      quote.totalMinor < 50 ||
      !quotePaymentIntentId?.startsWith('pi_') ||
      order.paymentStatus === 'notRequired'
    ) {
      throw new PaymentContractError('The confirmed Stripe order has an invalid payment binding.');
    }
  } else if (
    quote.paymentCollection !== 'not_required' ||
    quote.captureMethod !== 'none' ||
    quote.subtotalMinor !== 0 ||
    quote.taxMinor !== 0 ||
    quote.totalMinor !== 0 ||
    quotePaymentIntentId !== null ||
    order.paymentStatus !== 'notRequired' ||
    order.captureMethod !== 'none' ||
    order.stripeLivemode != null ||
    (order.refundedAmount ?? 0) !== 0
  ) {
    throw new PaymentContractError('The confirmed payment-free order has an invalid payment binding.');
  }
  if (!Array.isArray(order.items) || order.items.length !== quote.items.length) {
    throw new PaymentContractError('The confirmed order lines do not match its quote.');
  }
  const quotedLines = quote.items.map((line, index) =>
    exactQuoteLineBinding(line, quote.currency, index));
  const orderLines = order.items.map((line, index) =>
    exactOrderLineBinding(line, currency, index));
  if (
    quotedLines.reduce((sum, line) => sum + line.lineTotalMinor, 0) !==
    quote.subtotalMinor
  ) {
    throw new PaymentContractError('The stored quote line totals are inconsistent.');
  }
  if (JSON.stringify(orderLines) !== JSON.stringify(quotedLines)) {
    throw new PaymentContractError('The confirmed order lines do not match its quote.');
  }
  return { ...amounts, currency };
}

async function callV2(functions, name, data) {
  const callable = httpsCallable(functions, name);
  const result = await callable(data);
  return result?.data;
}

export async function createCheckoutQuoteV2(functions, request) {
  return parseCheckoutQuoteV2Response(
    await callV2(functions, 'createCheckoutQuoteV2', request),
    request?.checkoutAttemptId,
  );
}

export async function createPaymentIntentV2(functions, quote, expectedStripeMode) {
  const response = await callV2(functions, 'createPaymentIntentV2', {
    quoteId: quote.quoteId,
    platform: 'web',
  });
  return parsePaymentIntentV2Response(response, quote, expectedStripeMode);
}

export function parseFreeOrderV2Response(payload, quote) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new PaymentContractError('The order service returned an invalid response.');
  }
  if (
    !quote?.quoteId ||
    quote.paymentCollection !== 'not_required' ||
    quote.captureMethod !== 'none' ||
    quote.subtotalMinor !== 0 ||
    quote.taxMinor !== 0 ||
    quote.totalMinor !== 0
  ) {
    throw new PaymentContractError('An exact zero-total quote is required.');
  }
  const orderNumber = requireMinorAmount(
    payload.orderNumber,
    'orderNumber',
    { allowZero: false, maximum: Number.MAX_SAFE_INTEGER },
  );
  if (
    payload.quoteId !== quote.quoteId ||
    payload.checkoutAttemptId !== quote.checkoutAttemptId ||
    payload.quoteHash !== quote.quoteHash ||
    payload.orderId !== quote.quoteId ||
    payload.amountMinor !== 0 ||
    normalizeCurrency(payload.currency) !== quote.currency ||
    payload.paymentCollection !== 'not_required' ||
    payload.paymentStatus !== 'notRequired' ||
    payload.state !== 'completed' ||
    payload.paymentIntentId != null ||
    payload.clientSecret != null ||
    payload.stripeMode != null
  ) {
    throw new PaymentContractError(
      'The order response did not match the authorized zero-total quote.',
    );
  }
  return {
    quoteId: quote.quoteId,
    orderId: quote.quoteId,
    orderNumber,
    amountMinor: 0,
    currency: quote.currency,
    paymentCollection: 'not_required',
    paymentStatus: 'notRequired',
    state: 'completed',
  };
}

export async function submitFreeOrderV2(functions, quote) {
  const response = await callV2(functions, 'submitFreeOrderV2', {
    quoteId: quote.quoteId,
    platform: 'web',
  });
  return parseFreeOrderV2Response(response, quote);
}

export function parseActiveCheckoutV2Response(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new PaymentContractError('The active checkout service returned an invalid response.');
  }
  if (payload.active === false) return { active: false };
  if (payload.active !== true || typeof payload.recoverable !== 'boolean') {
    throw new PaymentContractError('The active checkout response is incomplete.');
  }
  const quote = parseCheckoutQuoteV2Response(payload);
  const state = requireNonEmptyString(payload.state, 'state');
  if (![
    'quoted',
    'intentCreating',
    'intentCreated',
    'processing',
    'failed',
    'paid',
    'authorized',
    'completed',
  ].includes(state)) {
    throw new PaymentContractError('The active checkout returned an unsupported state.');
  }
  const paymentIntentId = payload.paymentIntentId == null
    ? null
    : requireNonEmptyString(payload.paymentIntentId, 'paymentIntentId');
  if (paymentIntentId && !paymentIntentId.startsWith('pi_')) {
    throw new PaymentContractError('The active checkout payment binding is invalid.');
  }
  if (quote.paymentCollection === 'stripe') {
    if (state === 'completed' || (['paid', 'authorized'].includes(state) && !paymentIntentId)) {
      throw new PaymentContractError('The active Stripe checkout binding is invalid.');
    }
  } else if (
    paymentIntentId != null ||
    !['quoted', 'completed'].includes(state)
  ) {
    throw new PaymentContractError('The active payment-free checkout binding is invalid.');
  }
  const reason = payload.reason == null
    ? null
    : requireNonEmptyString(payload.reason, 'reason');
  return {
    ...quote,
    active: true,
    recoverable: payload.recoverable,
    state,
    paymentIntentId,
    reason,
  };
}

export async function getActiveCheckoutV2(functions) {
  return parseActiveCheckoutV2Response(
    await callV2(functions, 'getActiveCheckoutV2', {}),
  );
}

export function checkoutErrorMessage(error, fallback = 'Secure checkout is temporarily unavailable.') {
  if (error instanceof PaymentContractError) return error.message;

  const code = typeof error?.code === 'string' ? error.code : '';
  const safeCallableCodes = new Set([
    'functions/invalid-argument',
    'functions/failed-precondition',
    'functions/not-found',
    'functions/permission-denied',
    'functions/unauthenticated',
    'functions/already-exists',
    'functions/resource-exhausted',
  ]);
  if (safeCallableCodes.has(code) && typeof error?.message === 'string' && error.message.trim()) {
    return error.message.trim();
  }
  return fallback;
}

function browserPersistentStorage(storage) {
  if (storage !== undefined) return storage;
  if (typeof window === 'undefined') return null;
  return window.localStorage;
}

function readCheckoutAttempt(storage) {
  try {
    const target = browserPersistentStorage(storage);
    const raw = target?.getItem(CHECKOUT_ATTEMPT_STORAGE_KEY);
    if (typeof raw !== 'string' || !raw) return null;
    const value = JSON.parse(raw);
    if (
      value?.schemaVersion !== CHECKOUT_ATTEMPT_SCHEMA_VERSION ||
      !CHECKOUT_ATTEMPT_STATES.has(value?.state)
    ) return null;
    const attemptId = requireUUID(value.attemptId, 'attemptId');
    const quoteId = value.quoteId == null
      ? null
      : requireNonEmptyString(value.quoteId, 'quoteId');
    if (value.state !== 'preparing' && !quoteId) return null;
    return {
      schemaVersion: CHECKOUT_ATTEMPT_SCHEMA_VERSION,
      attemptId,
      quoteId,
      state: value.state,
    };
  } catch {
    return null;
  }
}

function writeCheckoutAttempt(target, record) {
  try {
    target.setItem(CHECKOUT_ATTEMPT_STORAGE_KEY, JSON.stringify(record));
    const persisted = readCheckoutAttempt(target);
    if (
      !persisted ||
      persisted.attemptId !== record.attemptId ||
      persisted.quoteId !== (record.quoteId ?? null) ||
      persisted.state !== record.state
    ) {
      throw new PaymentContractError('Secure checkout recovery state could not be persisted.');
    }
    return persisted;
  } catch (error) {
    if (error instanceof PaymentContractError) throw error;
    throw new PaymentContractError('Secure checkout recovery state could not be persisted.');
  }
}

export function getCheckoutAttempt(storage) {
  return readCheckoutAttempt(storage);
}

export function getOrCreateCheckoutAttempt(storage, idFactory) {
  const existing = readCheckoutAttempt(storage);
  if (existing) return existing;

  const target = browserPersistentStorage(storage);
  if (!target) {
    throw new PaymentContractError('Persistent browser storage is required for secure checkout.');
  }
  const factory = idFactory || (() => window.crypto?.randomUUID?.());
  const attemptId = requireUUID(factory(), 'checkoutAttemptId');
  return writeCheckoutAttempt(target, {
    schemaVersion: CHECKOUT_ATTEMPT_SCHEMA_VERSION,
    attemptId,
    quoteId: null,
    state: 'preparing',
  });
}

export function bindCheckoutAttemptQuote(attemptId, quoteId, storage) {
  const normalizedAttemptId = requireUUID(attemptId, 'checkoutAttemptId');
  const normalizedQuoteId = requireNonEmptyString(quoteId, 'quoteId');
  const target = browserPersistentStorage(storage);
  if (!target) {
    throw new PaymentContractError('Persistent browser storage is required for secure checkout.');
  }
  const existing = readCheckoutAttempt(target);
  if (existing && existing.attemptId !== normalizedAttemptId) {
    throw new PaymentContractError('Another checkout attempt is already active in this browser.');
  }
  if (existing?.quoteId && existing.quoteId !== normalizedQuoteId) {
    throw new PaymentContractError('The checkout attempt is already bound to another quote.');
  }
  return writeCheckoutAttempt(target, {
    schemaVersion: CHECKOUT_ATTEMPT_SCHEMA_VERSION,
    attemptId: normalizedAttemptId,
    quoteId: normalizedQuoteId,
    state: existing?.state === 'submitted' ? 'submitted' : 'ready',
  });
}

/** Replace a local cache with the server-owned binding discovered after login. */
export function adoptServerCheckoutAttempt(activeCheckout, storage) {
  if (!activeCheckout?.active) {
    throw new PaymentContractError('An active server checkout is required.');
  }
  const attemptId = requireUUID(activeCheckout.checkoutAttemptId, 'checkoutAttemptId');
  const quoteId = requireNonEmptyString(activeCheckout.quoteId, 'quoteId');
  const target = browserPersistentStorage(storage);
  if (!target) {
    throw new PaymentContractError('Persistent browser storage is required for secure checkout.');
  }
  const submitted = [
    'processing',
    'paid',
    'authorized',
    'completed',
  ].includes(activeCheckout.state);
  return writeCheckoutAttempt(target, {
    schemaVersion: CHECKOUT_ATTEMPT_SCHEMA_VERSION,
    attemptId,
    quoteId,
    state: submitted ? 'submitted' : 'ready',
  });
}

export function getSubmittedQuote(storage) {
  const attempt = readCheckoutAttempt(storage);
  return attempt?.state === 'submitted' ? attempt.quoteId : null;
}

export function getResumableQuote(storage) {
  const attempt = readCheckoutAttempt(storage);
  return attempt?.state === 'ready' ? attempt.quoteId : null;
}

/** Persist submission before Stripe can move money; confirmation must stop if this fails. */
export function markQuoteSubmitted(quoteId, storage) {
  const normalizedQuoteId = requireNonEmptyString(quoteId, 'quoteId');
  try {
    const target = browserPersistentStorage(storage);
    const attempt = readCheckoutAttempt(target);
    if (!target || !attempt || attempt.quoteId !== normalizedQuoteId) return false;
    writeCheckoutAttempt(target, { ...attempt, state: 'submitted' });
    return true;
  } catch {
    return false;
  }
}

export function clearSubmittedQuote(quoteId, storage) {
  const normalizedQuoteId = requireNonEmptyString(quoteId, 'quoteId');
  try {
    const target = browserPersistentStorage(storage);
    const attempt = readCheckoutAttempt(target);
    if (!target || !attempt || attempt.quoteId !== normalizedQuoteId) return false;
    writeCheckoutAttempt(target, { ...attempt, state: 'ready' });
    return true;
  } catch {
    return false;
  }
}

export function discardCheckoutAttempt(quoteId, storage) {
  const normalizedQuoteId = quoteId == null
    ? null
    : requireNonEmptyString(quoteId, 'quoteId');
  try {
    const target = browserPersistentStorage(storage);
    const attempt = readCheckoutAttempt(target);
    if (!target || !attempt) return false;
    if (normalizedQuoteId && attempt.quoteId !== normalizedQuoteId) return false;
    target.removeItem(CHECKOUT_ATTEMPT_STORAGE_KEY);
    return readCheckoutAttempt(target) == null;
  } catch {
    return false;
  }
}

export function consumeSubmittedQuote(quoteId, storage) {
  return discardCheckoutAttempt(quoteId, storage);
}

export function paymentIntentReturnDisposition(status) {
  switch (status) {
    case 'succeeded':
    case 'processing':
    case 'requires_capture':
      return 'submitted';
    case 'requires_payment_method':
      return 'retryable';
    case 'canceled':
      return 'restart';
    default:
      return 'pending';
  }
}
